/**
 * The containers a workspace declares for each member's machine (§5.9.1) —
 * defined here, carried by the workspace document, and checked and started by
 * each member's Airlock with their own container runner, on their click.
 *
 * A list like the other three on this page (`list-settings.tsx`): nothing above
 * it to inherit from, no source tag, no Reset. One row per service, Edit and
 * Remove on it, and a form under the list that adds one or replaces the row
 * being edited. A secret is never typed here: `${project/VARIABLE}` names a
 * project's variable, whose value each member's machine already holds.
 */
import { useState } from 'react';
import type React from 'react';
import { MAX_LOCAL_SERVICES, localServicesProblem, type LocalService } from '@airlock/contracts/settings/localservice';
import type { DocProject } from '@airlock/contracts/workspace/document';
import { color, font, radius } from '../../theme/tokens';
import { GhostButton, Input, RowAction } from '../../components/primitives';
import { BLANK_SERVICE, draftFromService, serviceFromDraft, type LocalServiceDraft } from './local-service-form';

const monoText: React.CSSProperties = { fontFamily: font.mono, fontSize: 12.5, color: color.inkBody };

export function LocalServiceList({
  value,
  repos,
  onChange,
  editable
}: {
  value: LocalService[];
  /** The workspace's projects — what a `${project/VARIABLE}` reference is resolved against. */
  repos: readonly DocProject[];
  /** An empty list is written as no key at all, which is how the document keeps it. */
  onChange: (next: LocalService[] | undefined) => void;
  editable?: boolean;
}): React.JSX.Element {
  const [draft, setDraft] = useState<LocalServiceDraft>(BLANK_SERVICE);
  const [editing, setEditing] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  const write = (next: LocalService[]): void => onChange(next.length ? next : undefined);

  const save = (): void => {
    if (!editing && value.length >= MAX_LOCAL_SERVICES) {
      setProblem(`A workspace declares ${MAX_LOCAL_SERVICES} local services at most.`);
      return;
    }
    // The app mints a row id the same way; this one names a declaration, and travels.
    const id = editing ?? `svc_${Math.random().toString(36).slice(2, 10)}`;
    const service = serviceFromDraft(draft, repos, id);
    if (typeof service === 'string') {
      setProblem(service);
      return;
    }
    const next = editing ? value.map((one) => (one.id === editing ? service : one)) : [...value, service];
    const refused = localServicesProblem(next);
    if (refused) {
      setProblem(refused);
      return;
    }
    write(next);
    setDraft(BLANK_SERVICE);
    setEditing(null);
    setProblem(null);
  };

  const field = (key: keyof LocalServiceDraft, label: string, placeholder: string, lines = 1): React.JSX.Element => (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: lines > 1 ? '1 1 100%' : '1 1 180px', minWidth: 0 }}>
      <span style={{ fontSize: 12, color: color.inkMuted }}>{label}</span>
      {lines > 1 ? (
        <textarea
          value={draft[key]}
          placeholder={placeholder}
          rows={lines}
          spellCheck={false}
          onChange={(event) => {
            setDraft((current) => ({ ...current, [key]: event.target.value }));
            setProblem(null);
          }}
          style={{
            fontFamily: font.mono,
            fontSize: 13,
            padding: '9px 12px',
            border: `1px solid ${color.lineInput}`,
            borderRadius: radius.lg,
            background: color.page,
            color: color.ink,
            resize: 'vertical'
          }}
        />
      ) : (
        <Input
          value={draft[key]}
          placeholder={placeholder}
          spellCheck={false}
          onChange={(event) => {
            setDraft((current) => ({ ...current, [key]: event.target.value }));
            setProblem(null);
          }}
          style={{ fontFamily: font.mono, fontSize: 13 }}
        />
      )}
    </label>
  );

  return (
    <div>
      {value.map((service) => (
        <div
          key={service.id}
          style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 0', boxShadow: `inset 0 -1px 0 ${color.hairSoft}` }}
        >
          <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span style={{ fontSize: 13.5, color: color.ink }}>
              {service.name} · <span style={monoText}>{service.image}</span>
            </span>
            <span style={{ ...monoText, fontSize: 11.5, color: color.inkFaint, overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {[
                service.ports.length ? `ports ${service.ports.map((port) => `${port.host}:${port.container}`).join(', ')}` : 'no port',
                service.env.length ? `${service.env.length} variable${service.env.length === 1 ? '' : 's'}` : '',
                service.env.some((entry) => entry.from) ? 'secrets by reference' : '',
                service.volumes.length ? `volumes ${service.volumes.map((volume) => volume.name).join(', ')}` : ''
              ]
                .filter(Boolean)
                .join(' · ')}
            </span>
          </span>
          {editable && (
            <>
              <RowAction
                onClick={() => {
                  setEditing(service.id);
                  setDraft(draftFromService(service, repos));
                  setProblem(null);
                }}
              >
                Edit
              </RowAction>
              <RowAction danger onClick={() => write(value.filter((one) => one.id !== service.id))}>
                Remove
              </RowAction>
            </>
          )}
        </div>
      ))}
      {!value.length && (
        <p style={{ margin: '4px 0 0', fontSize: 12.5, color: color.inkFaint }}>No container is declared.</p>
      )}
      {editable && (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 10,
            alignItems: 'flex-end',
            marginTop: 10,
            padding: 12,
            border: `1px solid ${color.line}`,
            borderRadius: radius.lg
          }}
        >
          {field('name', 'Name', 'postgres')}
          {field('image', 'Image', 'postgres:16')}
          {field('ports', 'Ports (machine:container)', '5432 or 15432:5432')}
          {field('env', 'Environment, one per line', 'POSTGRES_USER=app\nPOSTGRES_PASSWORD=${api/DB_PASSWORD}', 3)}
          {field('volumes', 'Named volumes, one per line', 'data:/var/lib/postgresql/data', 2)}
          <div style={{ display: 'flex', gap: 8 }}>
            <GhostButton onClick={save}>{editing ? 'Save service' : 'Add service'}</GhostButton>
            {editing && (
              <GhostButton
                onClick={() => {
                  setEditing(null);
                  setDraft(BLANK_SERVICE);
                  setProblem(null);
                }}
              >
                Cancel
              </GhostButton>
            )}
          </div>
        </div>
      )}
      {problem && <p style={{ margin: '8px 0 0', fontSize: 12, color: color.badText }}>{problem}</p>}
    </div>
  );
}
